import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, ElementRef, ViewChild } from '@angular/core';
import { GoogleMapsLoaderService } from '../../services/google-maps-loader.service';

export interface MapMarker {
  id: string | number;
  lat: number;
  lng: number;
  title: string;
  subtitle?: string;
  imageUrl?: string;
  color?: 'red' | 'blue' | 'yellow' | 'purple' | 'orange' | 'pink';
  distance?: string; // e.g. "5 km away"
  data: any;
}

export interface MapConfig {
  height?: string;
  mobileHeight?: string;
  defaultCenter?: { lat: number; lng: number };
  defaultZoom?: number;
  enableClustering?: boolean;
  directionsMode?: boolean; // When true, clicking marker opens Google Maps directions
}

@Component({
  selector: 'app-location-map',
  standalone: false,
  templateUrl: './location-map.html',
  styleUrl: './location-map.css'
})
export class LocationMap implements OnInit, OnChanges {
  @ViewChild('mapContainer', { static: true }) mapContainer!: ElementRef;

  @Input() markers: MapMarker[] = [];
  @Input() config: MapConfig = {};
  @Input() loading: boolean = false;

  @Output() markerClick = new EventEmitter<MapMarker>();
  @Output() infoWindowAction = new EventEmitter<MapMarker>();

  map: google.maps.Map | null = null;
  mapMarkers: google.maps.Marker[] = [];
  markerClusterer: any = null;
  activeInfoWindow: google.maps.InfoWindow | null = null;

  mapLoaded: boolean = false;
  mapError: string | null = null;

  displayedMarkersCount: number = 0;
  totalItemsCount: number = 0;

  private readonly DEFAULT_CENTER = { lat: 59.9139, lng: 10.7522 }; // Oslo, Norway
  private readonly DEFAULT_ZOOM = 6;

  constructor(private mapsLoader: GoogleMapsLoaderService) {}

  async ngOnInit() {
    try {
      await this.mapsLoader.load();
      await this.initializeMap();
    } catch (error) {
      console.error('Failed to load Google Maps:', error);
      this.mapError = 'Google Maps API key not configured. Please add your API key to the environment file.';
    }
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['markers'] && this.map) {
      await this.updateMarkers();
    }
  }

  private async initializeMap() {
    try {
      // Check if Google Maps is available
      if (typeof google === 'undefined' || !google.maps) {
        this.mapError = 'Google Maps not loaded. Please check your API key.';
        return;
      }

      const center = this.config.defaultCenter || this.DEFAULT_CENTER;
      const zoom = this.config.defaultZoom || this.DEFAULT_ZOOM;

      this.map = new google.maps.Map(this.mapContainer.nativeElement, {
        center,
        zoom,
        gestureHandling: 'cooperative',
        zoomControl: true,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true
      });

      this.mapLoaded = true;
      await this.updateMarkers();
    } catch (error) {
      console.error('Failed to initialize map:', error);
      this.mapError = 'Failed to load map. Please check your internet connection and API key.';
    }
  }

  private async updateMarkers() {
    if (!this.map) return;

    // Clear existing markers
    this.clearMarkers();

    this.totalItemsCount = this.markers.length;
    this.displayedMarkersCount = 0;

    const bounds = new google.maps.LatLngBounds();

    for (const marker of this.markers) {
      const position = { lat: marker.lat, lng: marker.lng };

      const markerOptions: google.maps.MarkerOptions = {
        map: this.map,
        position,
        title: marker.title
      };

      // Use colored pin marker
      if (marker.color) {
        markerOptions.icon = this.getColoredMarkerIcon(marker.color);
      }

      const mapMarker = new google.maps.Marker(markerOptions);

      // Add click listener
      mapMarker.addListener('click', () => {
        if (this.config.directionsMode) {
          // Open Google Maps directions to this location
          this.openDirections(marker);
        } else {
          this.showInfoWindow(marker, mapMarker);
        }
        this.markerClick.emit(marker);
      });

      this.mapMarkers.push(mapMarker);
      bounds.extend(position);
      this.displayedMarkersCount++;
    }

    // Fit map to show all markers
    if (this.mapMarkers.length > 0) {
      this.map.fitBounds(bounds);

      // Don't zoom in too much for single markers
      const listener = google.maps.event.addListener(this.map, 'idle', () => {
        const currentZoom = this.map?.getZoom();
        if (currentZoom && currentZoom > 15) {
          this.map?.setZoom(15);
        }
        google.maps.event.removeListener(listener);
      });
    }

    // Initialize clustering if enabled and have many markers
    if (this.config.enableClustering && this.mapMarkers.length > 10) {
      await this.initializeClustering();
    }
  }

  private async initializeClustering() {
    try {
      const { MarkerClusterer } = await import('@googlemaps/markerclusterer');

      if (this.markerClusterer) {
        this.markerClusterer.clearMarkers();
      }

      this.markerClusterer = new MarkerClusterer({
        map: this.map,
        markers: this.mapMarkers
      });
    } catch (error) {
      console.warn('Marker clustering not available:', error);
    }
  }

  private showInfoWindow(marker: MapMarker, mapMarker: google.maps.Marker) {
    // Close any existing info window
    if (this.activeInfoWindow) {
      this.activeInfoWindow.close();
    }

    const content = this.createInfoWindowContent(marker);

    this.activeInfoWindow = new google.maps.InfoWindow({
      content,
      ariaLabel: marker.title
    });

    this.activeInfoWindow.open({
      anchor: mapMarker,
      map: this.map
    });

    // Add click listener to the action button after info window opens
    google.maps.event.addListenerOnce(this.activeInfoWindow, 'domready', () => {
      const actionBtn = document.getElementById(`map-marker-action-${marker.id}`);
      if (actionBtn) {
        actionBtn.addEventListener('click', () => {
          this.infoWindowAction.emit(marker);
        });
      }
    });
  }

  private createInfoWindowContent(marker: MapMarker): string {
    const imageHtml = marker.imageUrl
      ? `<img src="${marker.imageUrl}" alt="${marker.title}" style="width: 48px; height: 48px; border-radius: 50%; object-fit: cover; border: 2px solid #fff; box-shadow: 0 2px 6px rgba(0,0,0,0.12);"/>`
      : `<div style="width: 48px; height: 48px; border-radius: 50%; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); display: flex; align-items: center; justify-content: center; border: 2px solid #fff; box-shadow: 0 2px 6px rgba(0,0,0,0.12);">
          <span style="color: white; font-size: 1rem; font-weight: 600;">${this.getInitials(marker.title)}</span>
        </div>`;

    const distanceHtml = marker.distance
      ? `<div style="display: inline-flex; align-items: center; gap: 4px; background: #f0f4f8; padding: 3px 8px; border-radius: 10px; font-size: 0.7rem; color: #4a5568; margin-bottom: 8px;">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
            <circle cx="12" cy="10" r="3"></circle>
          </svg>
          ${marker.distance}
        </div>`
      : '';

    return `
      <div style="min-width: 170px; max-width: 200px; padding: 6px 8px 8px; text-align: center; overflow: hidden;">
        <div style="display: flex; justify-content: center; margin-bottom: 4px;">
          ${imageHtml}
        </div>
        <h4 style="font-size: 0.85rem; font-weight: 600; margin: 0 0 2px 0; color: #1a1a2e; line-height: 1.1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${marker.title}</h4>
        ${marker.subtitle ? `<p style="font-size: 0.7rem; color: #6c757d; margin: 0 0 3px 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${marker.subtitle}</p>` : ''}
        ${distanceHtml}
        <button id="map-marker-action-${marker.id}" style="width: 100%; padding: 6px 10px; background: linear-gradient(135deg, #19226d 0%, #2d3a8c 100%); color: white; border: none; border-radius: 5px; font-size: 0.75rem; font-weight: 500; cursor: pointer;">
          View Details
        </button>
      </div>
    `;
  }

  private getInitials(name: string): string {
    if (!name) return '?';
    const words = name.split(' ').filter(w => w.length > 0);
    if (words.length >= 2) {
      return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }

  private clearMarkers() {
    // Remove markers from map
    for (const marker of this.mapMarkers) {
      marker.setMap(null);
    }
    this.mapMarkers = [];

    // Clear clusterer
    if (this.markerClusterer) {
      this.markerClusterer.clearMarkers();
    }

    // Close info window
    if (this.activeInfoWindow) {
      this.activeInfoWindow.close();
      this.activeInfoWindow = null;
    }
  }

  private openDirections(marker: MapMarker) {
    // Open Google Maps with directions to the marker location
    const destination = `${marker.lat},${marker.lng}`;
    const url = `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
    window.open(url, '_blank');
  }

  getMapHeight(): string {
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return this.config.mobileHeight || '300px';
    }
    return this.config.height || '400px';
  }

  private getColoredMarkerIcon(color: string): google.maps.Symbol {
    // Color map for marker colors
    const colorMap: { [key: string]: string } = {
      'red': '#e53935',
      'blue': '#1e88e5',
      'yellow': '#fdd835',
      'purple': '#8e24aa',
      'orange': '#fb8c00',
      'pink': '#d81b60'
    };

    const fillColor = colorMap[color] || colorMap['red'];

    return {
      path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z',
      fillColor: fillColor,
      fillOpacity: 1,
      strokeColor: '#ffffff',
      strokeWeight: 2,
      scale: 1.5,
      anchor: new google.maps.Point(12, 22)
    };
  }
}
